import { computed, ref } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { NAVIGATION_COMPANIES_API_URL } from '@/vue/helpers/apiURLS'
import { fetchApiData } from '@/vue/helpers/getApiData'
import type { BreadcrumbCompany } from './breadcrumb.types'
import type { NavigationApiCompany, NavigationCompaniesResponse } from '@/vue/types/navigation'

export const useCompanyList = createGlobalState(() => {
    const companyMap = ref<Map<number, BreadcrumbCompany>>(new Map())
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    async function fetchCompanies() {
        isLoading.value = true
        error.value = null

        try {
            const { data, error: apiError } = await fetchApiData<NavigationCompaniesResponse>(NAVIGATION_COMPANIES_API_URL)

            if (apiError) {
                error.value = apiError
                console.error('Failed to fetch companies:', apiError)
                return
            }

            const apiCompanies: NavigationApiCompany[] = (data?.data.companies || [])
                .filter((c): c is NavigationApiCompany => c !== null && c.name !== null)
                .sort((a, b) => a.name.localeCompare(b.name))

            apiCompanies.forEach((apiCompany) => {
                companyMap.value.set(apiCompany.id, {
                    id: apiCompany.id,
                    name: apiCompany.name,
                    type: 'company',
                    portfolios: [],
                    oldest_reporting_year: apiCompany.oldest_reporting_year,
                    last_reporting_year: apiCompany.last_reporting_year,
                    asset_only_access: apiCompany.asset_only_access,
                })
            })
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to fetch companies'
            console.error('Error fetching companies:', err)
        } finally {
            isLoading.value = false
        }
    }

    function getCompanyById(id: number): BreadcrumbCompany | null {
        return companyMap.value.get(id) || null
    }

    const companies = computed(() => Array.from(companyMap.value.values()))

    return {
        companies,
        companyMap,
        fetchCompanies,
        getCompanyById,
        isLoading,
        error,
    }
})
